import React, {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import snapshot from '../demo/snapshot.json';
import {
  day, geoName, money, num, paragraphs, pct, stateLabel, words,
} from '../format';
import Flag from './Flag';
import Logo from './Logo';
import SeriesChart from './SeriesChart';
import TrendTile from './TrendTile';
import { Mark, SearchIcon } from './icons';
import './HeroDemo.css';

/* The hero: the three pages of the product, on one frozen week of real data.
 *
 * Deliberately not a screenshot or a video. Both are pixels, so both are sharp
 * only at the size they were exported for, and a video of small UI text is the
 * worst case for a codec. These are the real components - the same TrendTile
 * the grid renders, the same chart the trend page draws - so it is vector at
 * every zoom and it cannot drift away from the product the way a screenshot
 * taken once and forgotten does.
 *
 * Frozen, though, rather than live. It reads a committed snapshot instead of
 * /api/preview for two reasons. The callouts name figures on the page - a
 * volume, a materiality score - and against live data those annotations are
 * wrong the week the data moves. And the landing page was waiting on a database
 * that autosuspends, so the first thing a visitor saw could be a blank frame
 * for half a minute. Now it is in the bundle: no request, no cold start.
 *
 * Nothing inside the frame is clickable. The tabs are, and they are the only
 * thing that is - a demo running its own script that also fights the pointer is
 * worse than one that plainly does not take input. */

const { coverage, trends, episode, company } = snapshot;

/* ── what each tab says, and what it points at ──────────────────────────────
 *
 * Callouts are anchored by selector rather than by a ref threaded through the
 * real components, which would mean adding demo-only props to TrendTile and the
 * rest. A selector that stops matching drops its callout and nothing else. */

const TABS = [
  {
    id: 'trends',
    label: 'Trending terms',
    nav: 'Trends',
    url: 'inflection.pro/trends',
    notes: [
      // Anchored down the right-hand column on purpose: a callout that points
      // back into the middle of the grid drags its line across two tiles to get
      // there, and the line is then the loudest thing on the page.
      {
        id: 'signal',
        sel: '.tw-grid > *:nth-child(3) .tw-spark',
        text: 'Robust statistics split signal from noise.',
      },
      {
        id: 'mat',
        sel: '.tw-grid > *:nth-child(6) .tw-tile-mat',
        text: 'Scored for how meaningful each term is.',
      },
    ],
    beats: [{ at: 1600, show: 'signal' }, { at: 6600, show: 'mat' },
      { at: 12000, done: true }],
  },
  {
    id: 'trend',
    label: 'Trend detail',
    nav: 'Trends',
    url: `inflection.pro/trends/${episode.episode.episode_id}`,
    // Ordered down the page, so the scroll only ever travels one way.
    notes: [
      {
        id: 'volume',
        sel: '[data-anchor="volume"]',
        text: 'Estimated monthly Google searches — absolute demand.',
      },
      {
        id: 'materiality',
        sel: '[data-anchor="materiality"]',
        text: 'Materiality: trend intensity, weighted by the brand\u2019s revenue '
          + 'contribution.',
      },
      {
        id: 'chart',
        sel: '[data-anchor="chart"]',
        text: 'Five years of trend data and search volume.',
      },
      {
        id: 'why',
        sel: '[data-anchor="why"]',
        text: 'And the write-up says what is driving it.',
      },
    ],
    beats: [{ at: 1600, show: 'volume' }, { at: 6600, show: 'materiality' },
      { at: 11600, show: 'chart' }, { at: 16600, show: 'why' },
      { at: 22000, done: true }],
  },
  {
    id: 'company',
    label: 'Company detail',
    nav: 'Companies',
    url: `inflection.pro/companies/${company.company.company_id}`,
    notes: [
      {
        id: 'brands',
        sel: '[data-anchor="brands"]',
        text: 'Revenue contribution by brand.',
      },
      {
        id: 'terms',
        sel: '[data-anchor="terms"]',
        text: 'Every term scanned weekly for this company.',
      },
      {
        id: 'competitors',
        sel: '[data-anchor="competitors"]',
        text: 'And who they are up against — linked wherever we cover the competitor too.',
      },
    ],
    beats: [{ at: 1600, show: 'brands' }, { at: 6600, show: 'terms' },
      { at: 11600, show: 'competitors' }, { at: 17000, done: true }],
  },
];

/* ── scenes ─────────────────────────────────────────────────────────────────
 *
 * Markup rather than the route components themselves, because those fetch, take
 * params and render a page-height layout. The class names are the product's own
 * though, so what is drawn here is styled by the same stylesheet the app uses
 * and cannot quietly diverge from it. */

function TrendsScene() {
  // The sparkline wants points; the snapshot stores bare values to keep the
  // bundle down, since the date is never read for a 52-week spark.
  const curves = useMemo(
    () => trends.map((t) => (t.spark || []).map((value) => ({ value }))),
    [],
  );

  return (
    <div className="tw-page">
      <div className="tw-page-head">
        <div>
          <h1 className="tw-page-title">Open trends</h1>
          <p className="tw-page-sub">
            Search terms that have broken out of their own normal range and are still
            there. Each one is read by a language model that explains what is driving
            it and scores how material it is to the company, and the list is ordered
            by that score. Week ending {day(snapshot.week_end)}.
          </p>
        </div>
        <span className="tw-page-count">{coverage.open_episodes}</span>
      </div>

      <div className="tw-grid">
        {trends.map((s, i) => (
          <TrendTile key={s.episode_id} signal={s} points={curves[i]} />
        ))}
      </div>
    </div>
  );
}

function TrendScene() {
  const e = episode.episode;
  const a = episode.analysis;

  return (
    <div className="tw-detail">
      <span className="tw-back">&larr; All trends</span>

      <div className="tw-detail-head">
        <h1 className="tw-h1">
          <Logo symbol={e.eodhd_symbol} name={e.company} ticker={e.ticker} size={34} />
          <span className="sym">{e.ticker}</span>
          {e.trend_term}
        </h1>
        <div className="tw-crumbs">
          <span className="tw-link">{e.company}</span>
          <span className="sep">/</span>
          <span className="tw-crumb-geo"><Flag geo={e.geo} />{geoName(e.geo)}</span>
          {e.brand && (<><span className="sep">/</span>{e.brand}</>)}
          {e.revenue_share_pct ? (
            <><span className="sep">/</span>{num(e.revenue_share_pct)}% of revenue</>
          ) : null}
        </div>
        <div className="tw-tags">
          <span className="tw-tag good">{stateLabel(e.state)}</span>
        </div>
      </div>

      <div className="tw-block">
        <div className="tw-kv">
          <div>
            <div className="v">{e.weeks_open}w <small>ago</small></div>
            <div className="k">Broke out</div>
          </div>
          <div>
            <div className="v" style={{ fontSize: 16 }}>{day(e.started_on)}</div>
            <div className="k">Started</div>
          </div>
          <div>
            <div className={`v ${e.pct_change > 0 ? 'up' : 'down'}`}>{pct(e.pct_change)}</div>
            <div className="k">Year on year</div>
          </div>
          <div>
            <div className="v">{num(e.break_z, 1)}</div>
            <div className="k">Break z-score</div>
          </div>
          <div data-anchor="volume">
            <div className="v">{Number(e.monthly_volume).toLocaleString()}</div>
            <div className="k">Est. searches a month</div>
          </div>
          <div data-anchor="materiality">
            <div className="v">{num(a.materiality_score)}</div>
            <div className="k">Materiality</div>
          </div>
        </div>
      </div>

      <div className="tw-block">
        <h2 className="tw-block-title">Five year search interest</h2>
        <div data-anchor="chart">
          <SeriesChart
            points={episode.points}
            markers={[{ time: e.started_on, text: 'Trend signal' }]}
            height={260}
          />
        </div>
      </div>

      <div className="tw-block">
        <h2 className="tw-block-title">What is happening</h2>
        <div className="tw-card tw-explain" data-anchor="why">
          <p className="tw-prose lead">{a.trend_driver}</p>
          {paragraphs(a.analysis).map((p, i) => (
            <p className="tw-prose" key={i}>{p}</p>
          ))}
          <div className="tw-note">
            {a.model} · written {day(a.created_at)} and carried forward while this
            trend stays open
          </div>
        </div>
      </div>
    </div>
  );
}

function CompanyScene() {
  const c = company.company;
  const { brands, scanned, competitors } = company;
  const linked = competitors.filter((x) => x.universe_company_id);

  return (
    <div className="tw-detail">
      <span className="tw-back">&larr; All companies</span>

      <div className="tw-detail-head">
        <h1 className="tw-h1">
          <Logo symbol={c.eodhd_symbol} name={c.name} ticker={c.ticker} size={34} />
          <span className="sym">{c.ticker}</span>
          {c.name}
        </h1>
        <div className="tw-crumbs">
          {[c.exchange, c.country, c.sector, c.industry].filter(Boolean).map((bit, i) => (
            <React.Fragment key={bit}>
              {i > 0 && <span className="sep">/</span>}
              {bit}
            </React.Fragment>
          ))}
        </div>
        {c.category_name && <div className="tw-subtitle">{c.category_name}</div>}
      </div>

      <div className="tw-block">
        <div className="tw-kv">
          <div>
            <div className="v">{money(c.market_cap_usd)}</div>
            <div className="k">Market cap</div>
          </div>
          <div>
            <div className="v">{scanned.length}</div>
            <div className="k">Terms scanned weekly</div>
          </div>
          <div>
            <div className="v">{brands.length}</div>
            <div className="k">Brands covered</div>
          </div>
        </div>
      </div>

      <div className="tw-block" data-anchor="brands">
        <h2 className="tw-block-title">Brands</h2>
        <div className="tw-table-wrap">
          <table className="tw-table">
            <thead>
              <tr><th>Brand</th><th colSpan={2}>Share of revenue</th><th>Basis</th></tr>
            </thead>
            <tbody>
              {brands.map((b) => (
                <tr key={b.brand_id}>
                  <td className="lead" style={{ width: 200 }}>{b.brand}</td>
                  <td className="tw-bar-cell" style={{ width: '42%' }}>
                    <div className="tw-bar-track">
                      <div className="tw-bar-fill"
                        style={{ width: `${Math.max(2, Math.min(100, b.revenue_share_pct))}%` }} />
                    </div>
                  </td>
                  <td className="n lead" style={{ width: 64 }}>{num(b.revenue_share_pct)}%</td>
                  <td style={{ fontSize: 12 }}>{words(b.revenue_share_basis)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="tw-block" data-anchor="terms">
        <h2 className="tw-block-title">Terms scanned weekly ({scanned.length})</h2>
        <div className="tw-table-wrap">
          <table className="tw-table">
            <thead>
              <tr>
                <th>Term</th><th>Market</th><th>Role</th>
                <th className="n">Proxy</th><th className="n">Latest</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {scanned.map((s) => (
                <tr key={s.series_id} className="clickable">
                  <td className="lead">{s.term}</td>
                  <td><Flag geo={s.geo} /></td>
                  <td>{words(s.term_role)}</td>
                  <td className="n">{s.demand_proxy_quality ?? '—'}</td>
                  <td className="n">{num(s.latest_value)}</td>
                  <td>
                    {s.episode_id
                      ? <span style={{ color: 'var(--up)' }}>{stateLabel(s.episode_state)}</span>
                      : words(s.status) || 'not yet scanned'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="tw-block" data-anchor="competitors">
        <h2 className="tw-block-title">
          Competitors — {linked.length} of {competitors.length} also covered
        </h2>
        <div className="tw-table-wrap">
          <table className="tw-table">
            <thead>
              <tr><th>Brand</th><th>Competitor</th><th>Covered</th></tr>
            </thead>
            <tbody>
              {competitors.map((x) => (
                <tr key={x.competitor_id} className={x.universe_company_id ? 'clickable' : ''}>
                  <td className="lead" style={{ width: 200 }}>{x.brand}</td>
                  <td style={{ width: 230 }}>{x.competitor_name || ''}</td>
                  <td>
                    {x.universe_company_id
                      ? <span style={{ color: 'var(--accent)' }}>{x.universe_ticker}</span>
                      : (
                        <span style={{ color: 'var(--fg-dim)' }}>
                          {x.listed ? 'Listed, not covered' : 'Private'}
                        </span>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const SCENES = { trends: TrendsScene, trend: TrendScene, company: CompanyScene };

/* ── the demo ───────────────────────────────────────────────────────────── */

export default function HeroDemo() {
  const [tabIndex, setTabIndex] = useState(0);
  const [shown, setShown] = useState([]);
  const [offset, setOffset] = useState(0);
  const [anchors, setAnchors] = useState({});

  const viewRef = useRef(null);
  const scrollRef = useRef(null);
  const timers = useRef([]);
  /* The same measurements the callouts render from, kept where the script can
   * read them without depending on them. Through state, every remeasure - and
   * the chart and the logos each cause one - would be a new bring(), a new
   * effect and a beat timeline restarted from zero. */
  const anchorsRef = useRef({});

  const tab = TABS[tabIndex];
  const Scene = SCENES[tab.id];

  /* Callout positions live in the content's own coordinates, not the
   * viewport's, so they ride the scroll instead of being chased down it. Both
   * rects sit under the same transform, so subtracting one from the other takes
   * the scroll back out. */
  const measure = useCallback(() => {
    const scroll = scrollRef.current;
    if (!scroll) return;
    const base = scroll.getBoundingClientRect();
    const found = {};
    tab.notes.forEach((n) => {
      const el = scroll.querySelector(n.sel);
      if (!el) return;
      const r = el.getBoundingClientRect();
      found[n.id] = {
        top: r.top - base.top,
        mid: r.top - base.top + r.height / 2,
        left: r.left - base.left,
        right: r.right - base.left,
      };
    });
    anchorsRef.current = found;
    setAnchors(found);
  }, [tab]);

  // The chart mounts late and the logos load later still, both of which move
  // everything below them, so measuring once on mount is measuring the wrong
  // layout.
  useLayoutEffect(() => {
    measure();
    const scroll = scrollRef.current;
    if (!scroll) return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(scroll);
    return () => ro.disconnect();
  }, [measure]);

  useEffect(() => {
    setShown([]);
    setOffset(0);
  }, [tabIndex]);

  // Scroll so the thing being talked about is in the upper half of the frame,
  // where the callout beside it has room.
  const bring = useCallback((id) => {
    const view = viewRef.current;
    const scroll = scrollRef.current;
    const a = anchorsRef.current[id];
    if (!view || !scroll || !a) return;
    const viewH = view.clientHeight;
    const max = Math.max(0, scroll.offsetHeight - viewH);

    setOffset((now) => {
      // Already on screen and not against an edge: leave it alone. Centring
      // something that was perfectly visible costs a 50px nudge that shears
      // the page heading in half and buys nothing.
      const sits = a.mid - now;
      if (sits > viewH * 0.12 && sits < viewH * 0.78) return now;
      return Math.min(max, Math.max(0, a.mid - viewH * 0.42));
    });
  }, []);

  useEffect(() => {
    const play = () => {
      timers.current.forEach(clearTimeout);
      timers.current = tab.beats.map((b) => setTimeout(() => {
        // Back to the top of this tab and round again. The tab itself only
        // ever changes when someone clicks one: a hero that advances itself
        // takes the controls back off the reader mid-read, and the whole point
        // of the tabs is that they choose what they are looking at.
        if (b.done) {
          setShown([]);
          setOffset(0);
          play();
          return;
        }
        // One at a time. Several of the things worth pointing at sit on the
        // same row - the volume and the materiality are two cells apart - and
        // callouts left on screen together end up stacked on top of each other.
        setShown([b.show]);
        bring(b.show);
      }, b.at));
    };

    const stop = () => timers.current.forEach(clearTimeout);
    // A loop ticking behind a hidden tab is waste, and it also means the demo
    // is halfway through rather than at the start when someone comes back.
    const onVisibility = () => {
      if (document.hidden) stop();
      else { setShown([]); setOffset(0); play(); }
    };

    play();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [tab, bring]);

  return (
    <div className="hd-wrap">
      {/* The browser chrome moved in here from the landing page so the address
        * changes with the tab. Three pages is the claim; showing three paths is
        * cheaper than saying it. */}
      <div className="preview-frame">
        <div className="preview-mock-body">
          <div className="mock-topbar">
            <span className="mock-dot" />
            <span className="mock-dot" />
            <span className="mock-dot" />
            <div className="mock-pill">{tab.url}</div>
          </div>

          <div className="hd tw" aria-hidden="true">
            <div className="hd-bar">
              <span className="hd-brand"><Mark />Inflection</span>
              <span className="hd-nav">
                {['Trends', 'Companies'].map((n) => (
                  <span key={n} className={n === tab.nav ? 'is-on' : ''}>{n}</span>
                ))}
              </span>
              <span className="hd-search">
                <SearchIcon />
                Search companies, brands and terms
              </span>
              <span className="hd-spacer" />
              <span className="hd-stats">
                <span><b>{coverage.companies.toLocaleString()}</b> companies</span>
                <span><b>{coverage.series.toLocaleString()}</b> terms weekly</span>
                <span><i className="tw-live" /><b>{coverage.open_episodes}</b> open</span>
              </span>
            </div>

            <div className="hd-view" ref={viewRef}>
              <div
                className="hd-scroll"
                ref={scrollRef}
                style={{ transform: `translateY(${-offset}px)` }}
              >
                <Scene />

                {tab.notes.map((n) => {
                  const a = anchors[n.id];
                  if (!a) return null;
                  return (
                    <div
                      key={n.id}
                      className={`hd-note ${shown.includes(n.id) ? 'is-on' : ''}`}
                      style={{ top: a.mid }}
                    >
                      <span className="hd-note-dot" style={{ left: a.right }} />
                      <span
                        className="hd-note-line"
                        style={{
                          left: a.right,
                          width: `max(10px, calc(100% - var(--note-gap) - var(--note-w) - ${a.right}px))`,
                        }}
                      />
                      <span className="hd-note-text">{n.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Under the frame, not over it. Above, they read as navigation for the
        * marketing site - three more places to go - and nothing tied them to
        * the window they actually drive. */}
      <div className="hd-tabs" role="tablist" aria-label="Pages of the product">
        {TABS.map((t, i) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={i === tabIndex}
            className={`hd-tab ${i === tabIndex ? 'is-on' : ''}`}
            onClick={() => setTabIndex(i)}
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}
